import { useState } from "react";
import { format } from "date-fns";
import { CalendarClock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { slotLabel } from "@/components/viewings/viewing_utils";

// Approve / Decline / Counter propose row for the RECEIVING side of a
// proposed viewing. Shared by the chat viewing panel, the viewings card and
// the in chat viewing card so the decision UI never diverges between
// surfaces.
//
// Two wordings, one behavior:
//   default        Approve / Decline / Counter propose, with the inline
//                  datetime-local picker (calendar page, viewings card).
//   variant="card" Confirm / Reject / Modify, per the founder's spec for the
//                  chat card. Modify is a two step flow: first pick WHAT to
//                  change ("Change the time" or "Change the date"), then the
//                  matching picker prefilled from the current proposal. Both
//                  paths submit through the same /counter endpoint.
//
// Approving happens by tapping a slot (that IS the approval when several
// times are offered).
export default function ViewingActions({ viewing, onUpdate, compact = false, variant = "default" }) {
  const [counterOpen, setCounterOpen] = useState(false);
  const [counterValue, setCounterValue] = useState("");
  // Modify two step state (card variant only): null | "choose" | "time" | "date"
  const [modifyStep, setModifyStep] = useState(null);
  const [modifyValue, setModifyValue] = useState("");
  const [busy, setBusy] = useState(false);

  const isCard = variant === "card";
  const slots = viewing.slots || [];
  // The proposal on the table anchors the Modify pickers: changing only the
  // time keeps this date, changing only the date keeps this time.
  const baseSlot = slots[0] ? new Date(slots[0]) : new Date();

  async function decide(path, body, successMessage) {
    setBusy(true);
    try {
      const res = await api.post(`/viewings/${viewing.id}/${path}`, body);
      onUpdate?.(res.data?.viewing || res.data);
      toast.success(successMessage);
      return true;
    } catch {
      toast.warning("That did not go through. Please try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  function approve(slot) {
    decide("approve", { slot }, "Viewing confirmed. See you there.");
  }

  function decline() {
    decide("decline", {}, isCard ? "Viewing rejected." : "Viewing declined.");
  }

  function sendCounter() {
    if (!counterValue) {
      toast.warning("Pick a date and time first.");
      return;
    }
    decide(
      "counter",
      { slot: new Date(counterValue).toISOString() },
      "Your suggested time was sent."
    ).then((ok) => {
      if (ok) {
        setCounterOpen(false);
        setCounterValue("");
      }
    });
  }

  function openModify(step) {
    setModifyStep(step);
    if (step === "time") setModifyValue(format(baseSlot, "HH:mm"));
    if (step === "date") setModifyValue(format(baseSlot, "yyyy-MM-dd"));
  }

  function sendModify() {
    if (!modifyValue) {
      toast.warning(modifyStep === "time" ? "Pick a time first." : "Pick a date first.");
      return;
    }
    // Merge the single changed part into the proposal on the table so the
    // untouched part carries over exactly.
    const next = new Date(baseSlot);
    if (modifyStep === "time") {
      const [h, m] = modifyValue.split(":").map(Number);
      next.setHours(h, m, 0, 0);
    } else {
      const [y, mo, d] = modifyValue.split("-").map(Number);
      next.setFullYear(y, mo - 1, d);
    }
    decide(
      "counter",
      { slot: next.toISOString() },
      "Your suggested time was sent."
    ).then((ok) => {
      if (ok) {
        setModifyStep(null);
        setModifyValue("");
      }
    });
  }

  const sizing = compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-xs";

  if (isCard) {
    return (
      <div>
        <div className="flex flex-wrap items-center gap-2">
          {slots.map((slot) => (
            <button
              key={slot}
              type="button"
              disabled={busy}
              onClick={() => approve(slot)}
              className={`rounded-full bg-brand-primary font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-50 ${sizing}`}
            >
              {slots.length === 1 ? "Confirm" : `Confirm ${slotLabel(slot)}`}
            </button>
          ))}
          <button
            type="button"
            disabled={busy}
            onClick={() => setModifyStep((s) => (s ? null : "choose"))}
            className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-text transition hover:border-brand-primary hover:text-brand-primary disabled:opacity-50 ${sizing}`}
          >
            Modify
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={decline}
            className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-sub transition hover:border-brand-primary hover:text-night-text disabled:opacity-50 ${sizing}`}
          >
            Reject
          </button>
          {busy && <Loader2 size={13} className="animate-spin text-night-sub" />}
        </div>

        {modifyStep === "choose" && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-night-sub">What would you like to change?</span>
            <button
              type="button"
              disabled={busy}
              onClick={() => openModify("time")}
              className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-text transition hover:border-brand-primary hover:text-brand-primary disabled:opacity-50 ${sizing}`}
            >
              Change the time
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => openModify("date")}
              className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-text transition hover:border-brand-primary hover:text-brand-primary disabled:opacity-50 ${sizing}`}
            >
              Change the date
            </button>
          </div>
        )}

        {(modifyStep === "time" || modifyStep === "date") && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 text-xs font-medium text-night-sub">
              <CalendarClock size={13} className="text-brand-primary" />
              {modifyStep === "time" ? `New time on ${format(baseSlot, "EEE MMM d")}` : `New date at ${format(baseSlot, "h:mm a")}`}
            </span>
            <input
              type={modifyStep === "time" ? "time" : "date"}
              value={modifyValue}
              onChange={(e) => setModifyValue(e.target.value)}
              className="min-w-0 rounded-xl border border-night-line bg-night px-3 py-1.5 text-xs text-night-text outline-none transition focus:border-brand-primary"
            />
            <button
              type="button"
              disabled={busy}
              onClick={sendModify}
              className="rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-50"
            >
              Send
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setModifyStep("choose")}
              className="text-xs font-semibold text-night-sub underline-offset-2 transition hover:text-night-text hover:underline"
            >
              Back
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {slots.map((slot) => (
          <button
            key={slot}
            type="button"
            disabled={busy}
            onClick={() => approve(slot)}
            className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-text transition hover:border-brand-primary hover:text-brand-primary disabled:opacity-50 ${sizing}`}
          >
            {slots.length === 1 ? `Approve ${slotLabel(slot)}` : slotLabel(slot)}
          </button>
        ))}
        <button
          type="button"
          disabled={busy}
          onClick={() => setCounterOpen((v) => !v)}
          className={`rounded-full border border-night-line bg-night-surface font-semibold text-night-text transition hover:border-brand-primary disabled:opacity-50 ${sizing}`}
        >
          Counter propose
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={decline}
          className={`font-semibold text-night-sub underline-offset-2 transition hover:text-night-text hover:underline disabled:opacity-50 ${compact ? "text-[11px]" : "text-xs"}`}
        >
          Decline
        </button>
        {busy && <Loader2 size={13} className="animate-spin text-night-sub" />}
      </div>

      {counterOpen && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-night-sub">
            <CalendarClock size={13} className="text-brand-primary" /> Suggest
          </span>
          <input
            type="datetime-local"
            value={counterValue}
            onChange={(e) => setCounterValue(e.target.value)}
            className="min-w-0 rounded-xl border border-night-line bg-night px-3 py-1.5 text-xs text-night-text outline-none transition focus:border-brand-primary"
          />
          <button
            type="button"
            disabled={busy}
            onClick={sendCounter}
            className="rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-50"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}
